import { useState } from "react";
import { Box, Typography, useTheme, Tab } from "@mui/material";
import TrendByCompetitorGraph from "../Graphs/TrendByCompetitorGraph";
import { OverviewCharts, TrafficData } from "~src/types/brand";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import { generateRevenueGraphData } from "../utils";
import { BrandResult } from "../SearchBar";

type PropsType = {
  brandTrafficData: TrafficData[];
  brandMetricsData: OverviewCharts[];
  selectedBrands: BrandResult[] | undefined;
};
export type MetricData = {
  date: string;
  data: number;
};

export type MetricArrayData = {
  date: string[];
  data: number[];
};

export type MetricObject = {
  facebook_likes: MetricArrayData;
  instagram_followers: MetricArrayData;
  trustpilot_reviews: MetricArrayData;
  reddit_mentions: MetricArrayData;
  facebook_ad_count: MetricArrayData;
  website_traffic: MetricArrayData;
};

export default function TrendByCompetitors({
  brandTrafficData,
  brandMetricsData,
  selectedBrands,
}: PropsType) {
  type MetricData = {
    date: string;
    data: number;
  };

  type OverviewCharts = {
    facebook_likes: MetricData[];
    instagram_followers: MetricData[];
    trustpilot_reviews: MetricData[];
    reddit_mentions: MetricData[];
    facebook_ad_count: MetricData[];
    website_traffic: MetricData[];
  };

  type MetricObject = {
    facebook_likes: { date: string[]; data: number[] };
    instagram_followers: { date: string[]; data: number[] };
    trustpilot_reviews: { date: string[]; data: number[] };
    reddit_mentions: { date: string[]; data: number[] };
    facebook_ad_count: { date: string[]; data: number[] };
    website_traffic: { date: string[]; data: number[] };
  };

  function transformData(inputData: OverviewCharts[]): MetricObject[] {
    const transformedData: MetricObject[] = [];

    inputData.forEach((item: OverviewCharts) => {
      const transformedItem: MetricObject = {
        facebook_likes: { date: [], data: [] },
        instagram_followers: { date: [], data: [] },
        trustpilot_reviews: { date: [], data: [] },
        reddit_mentions: { date: [], data: [] },
        facebook_ad_count: { date: [], data: [] },
        website_traffic: { date: [], data: [] },
      };

      const metricKeys: (keyof OverviewCharts)[] = [
        "facebook_likes",
        "instagram_followers",
        "trustpilot_reviews",
        "reddit_mentions",
        "facebook_ad_count",
        "website_traffic",
      ];

      metricKeys.forEach((key) => {
        const sortedMetrics = item[key].sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
        );

        sortedMetrics.forEach((metric) => {
          transformedItem[key].date.push(metric.date);
          transformedItem[key].data.push(metric.data);
        });
      });

      transformedData.push(transformedItem);
    });

    return transformedData;
  }

  const [tabValue, setTabValue] = useState("1");

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const theme = useTheme();

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.common.white,
        p: 4,
        mt: 2,
        borderRadius: "12px",
        border: 1,
        borderColor: theme.palette.grey[200],
      }}
    >
      <Typography variant="h5">Trend By Competitors</Typography>

      <TabContext value={tabValue}>
        <Box>
          <TabList onChange={handleChange} aria-label="lab API tabs example">
            <Tab label="Traffic" value="1" />
            <Tab label="Revenue" value="2" />
            <Tab label="Ads" value="3" />
            <Tab label="Facebook" value="4" />
            <Tab label="Instagram" value="5" />
          </TabList>
        </Box>
        <TabPanel value="1">
          <TrendByCompetitorGraph
            selectedBrands={selectedBrands}
            metricsChartData={transformData(brandMetricsData).map(
              (data) => data.website_traffic,
            )}
          />
        </TabPanel>
        <TabPanel value="2">
          <TrendByCompetitorGraph
            selectedBrands={selectedBrands}
            metricsChartData={generateRevenueGraphData(brandTrafficData).map(
              (data) => data,
            )}
          />
        </TabPanel>
        <TabPanel value="3">
          <TrendByCompetitorGraph
            selectedBrands={selectedBrands}
            metricsChartData={transformData(brandMetricsData).map(
              (data) => data.facebook_ad_count,
            )}
          />
        </TabPanel>
        <TabPanel value="4">
          <TrendByCompetitorGraph
            selectedBrands={selectedBrands}
            metricsChartData={transformData(brandMetricsData).map(
              (data) => data.facebook_likes,
            )}
          />
        </TabPanel>
        <TabPanel value="5">
          <TrendByCompetitorGraph
            selectedBrands={selectedBrands}
            metricsChartData={transformData(brandMetricsData).map(
              (data) => data.instagram_followers,
            )}
          />
        </TabPanel>
      </TabContext>
    </Box>
  );
}
